import { housingSystem } from './HousingSystem';
/**
 * TilePropertySystem.ts
 * Comprehensive Tactical Tile Property & Terrain Physics Engine for Terra Anima.
 *
 * Key Mechanics:
 * 1. Impassable Tiles (`walkable: false`):
 *    - `water` (강/호수/바다), `stone_wall` (성벽), `house_roof` (건물), `megalith` (거석), `fence` (울타리)
 *    - `bridge` (다리) overrides water to allow safe crossing.
 * 2. Hazard & DOT Tiles:
 *    - `lava` (불/용암 타일): Instant Fire Damage (18/s) + Applies Burning DOT (10 dmg/s for 4s) + Move Speed -15%.
 *    - `swamp` (독늪 타일): Move Speed -35% + Applies Poison Mire DOT (6 dmg/s for 3s) + Evade -10%.
 * 3. Directional Slope Tiles (`hills_grass` 산지/구릉지 타일):
 *    - Uphill (올라가는 쪽): Move Speed -20% (0.80x)
 *    - Downhill (내려가는 쪽): Move Speed +20% (1.20x)
 * 4. Tactical & Environmental Buff/Debuff Tiles:
 *    - `cobblestone` (포장 가도: 이속 +15%, SP회복 +25%), `dirt_road` (흙길: 이속 +8%),
 *      `ice` (빙판: 이속 +25% 미끄러짐, 회피 -15%), `snow` (설원: 이속 -15%, SP소모),
 *      `sand` (사막 모래: 이속 -12%), `altar` (고대 제단: HP/MP 회복 + 마법증폭 +15%),
 *      `crystal_floor` (성역 크리스탈: 이속 +10%, MP회복 + 시전가속) 등 21종 전 타일 완비.
 */

import { EntityState } from '../../core/Types';
import { ZoneData, ZoneTileType } from './ZoneMapData';

export interface TilePropertySpec {
  type: ZoneTileType;
  nameKr: string;
  icon: string;
  walkable: boolean;
  blocksProjectiles: boolean;
  baseSpeedMult: number;        // 1.0 = 100%, 0.8 = -20%, 1.15 = +15%
  isDirectionalSlope?: boolean; // True for `hills_grass`: Uphill -20% / Downhill +20%
  directDamagePerSec?: number;  // e.g. Lava instant burn
  dotEffect?: {
    name: string;
    damagePerSec: number;
    duration: number;
  };
  hpRegenPerSec?: number;
  mpRegenPerSec?: number;
  spDrainPerSec?: number;
  evadeBonusPct?: number;       // e.g. +12% in tall swamp grass, -15% on slippery ice
  magicAmpPct?: number;         // e.g. +15% on ancient rune altar
  badgeColor: string;
  summaryKr: string;
}

export const TILE_PROPERTY_SPECS: Record<ZoneTileType, TilePropertySpec> = {
  grass: {
    type: 'grass',
    nameKr: '평원 초지',
    icon: '🌿',
    walkable: true,
    blocksProjectiles: false,
    baseSpeedMult: 1.0,
    badgeColor: '#4ade80',
    summaryKr: '이동속도 100% (표준 평지 지형)',
  },
  hills_grass: {
    type: 'hills_grass',
    nameKr: '산지 경사면',
    icon: '⛰️',
    walkable: true,
    blocksProjectiles: false,
    baseSpeedMult: 1.0,
    isDirectionalSlope: true,
    badgeColor: '#a3e635',
    summaryKr: '오르막(올라갈 때) 이속 -20% / 내리막(내려갈 때) 이속 +20%',
  },
  cobblestone: {
    type: 'cobblestone',
    nameKr: '왕도 조약돌 가도',
    icon: '🛣️',
    walkable: true,
    blocksProjectiles: false,
    baseSpeedMult: 1.15,
    badgeColor: '#fde047',
    summaryKr: '이동속도 +15% (포장 도로 쾌속 기동)',
  },
  dirt_road: {
    type: 'dirt_road',
    nameKr: '다져진 흙길',
    icon: '🛤️',
    walkable: true,
    blocksProjectiles: false,
    baseSpeedMult: 1.08,
    badgeColor: '#fbbf24',
    summaryKr: '이동속도 +8% (오솔길 기동 보너스)',
  },
  water: {
    type: 'water',
    nameKr: '깊은 물 (강/호수)',
    icon: '🌊',
    walkable: false,
    blocksProjectiles: false,
    baseSpeedMult: 0,
    badgeColor: '#38bdf8',
    summaryKr: '🚫 이동 불가 (다리[Bridge]를 통해서만 도하 가능)',
  },
  bridge: {
    type: 'bridge',
    nameKr: '도하 교량 (다리)',
    icon: '🌉',
    walkable: true,
    blocksProjectiles: false,
    baseSpeedMult: 1.05,
    evadeBonusPct: -5,
    badgeColor: '#f59e0b',
    summaryKr: '물 지형 통과 가능 · 이동속도 +5% (좁은 교량 회피 -5%)',
  },
  lava: {
    type: 'lava',
    nameKr: '불/용암 지대',
    icon: '🌋',
    walkable: true,
    blocksProjectiles: false,
    baseSpeedMult: 0.85,
    directDamagePerSec: 18,
    dotEffect: {
      name: '🔥 용암 화상 (Burn DOT)',
      damagePerSec: 10,
      duration: 4.0,
    },
    badgeColor: '#ef4444',
    summaryKr: '🔥 위험! 초당 18 화염 피해 + 화상 DOT(초당 10, 4초간) + 이속 -15%',
  },
  swamp: {
    type: 'swamp',
    nameKr: '진흙 독늪',
    icon: '☠️',
    walkable: true,
    blocksProjectiles: false,
    baseSpeedMult: 0.65,
    directDamagePerSec: 4,
    dotEffect: {
      name: '☠️ 늪지 맹독 (Poison DOT)',
      damagePerSec: 6,
      duration: 3.5,
    },
    evadeBonusPct: -10,
    badgeColor: '#a855f7',
    summaryKr: '☠️ 수렁! 이동속도 -35% + 중독 DOT(초당 6, 3.5초간) + 회피 -10%',
  },
  swamp_grass: {
    type: 'swamp_grass',
    nameKr: '습지 갈대밭',
    icon: '🌾',
    walkable: true,
    blocksProjectiles: false,
    baseSpeedMult: 0.85,
    evadeBonusPct: 12,
    badgeColor: '#10b981',
    summaryKr: '이동속도 -15% · 갈대 은폐 효과로 회피율 +12%',
  },
  ice: {
    type: 'ice',
    nameKr: '빙판 타일',
    icon: '🧊',
    walkable: true,
    blocksProjectiles: false,
    baseSpeedMult: 1.25,
    evadeBonusPct: -15,
    badgeColor: '#67e8f9',
    summaryKr: '🧊 미끄러짐 가속 이속 +25% · 발이 미끄러워 회피율 -15%',
  },
  snow: {
    type: 'snow',
    nameKr: '만년설 눈밭',
    icon: '❄️',
    walkable: true,
    blocksProjectiles: false,
    baseSpeedMult: 0.85,
    spDrainPerSec: 2.5,
    badgeColor: '#bae6fd',
    summaryKr: '❄️ 깊은 눈! 이동속도 -15% · 한기로 초당 기력(SP) -2.5 소모',
  },
  sand: {
    type: 'sand',
    nameKr: '사막 모래밭',
    icon: '🏜️',
    walkable: true,
    blocksProjectiles: false,
    baseSpeedMult: 0.88,
    spDrainPerSec: 1.5,
    badgeColor: '#fb923c',
    summaryKr: '🏜️ 모래 빠짐! 이동속도 -12% · 열기로 초당 기력(SP) -1.5 소모',
  },
  farmland: {
    type: 'farmland',
    nameKr: '황금 밀밭 농경지',
    icon: '🌾',
    walkable: true,
    blocksProjectiles: false,
    baseSpeedMult: 0.95,
    hpRegenPerSec: 4,
    badgeColor: '#facc15',
    summaryKr: '이동속도 -5% · 풍요로운 들판 효과로 초당 HP +4 자연회복',
  },
  stone_pavement: {
    type: 'stone_pavement',
    nameKr: '성내 석조 보도',
    icon: '🏛️',
    walkable: true,
    blocksProjectiles: false,
    baseSpeedMult: 1.10,
    evadeBonusPct: 5,
    badgeColor: '#cbd5e1',
    summaryKr: '이동속도 +10% · 정돈된 석조 바닥으로 회피/방어 +5%',
  },
  castle_gate: {
    type: 'castle_gate',
    nameKr: '성문 요새 관문',
    icon: '🏰',
    walkable: true,
    blocksProjectiles: false,
    baseSpeedMult: 1.0,
    evadeBonusPct: 15,
    badgeColor: '#38bdf8',
    summaryKr: '이동속도 100% · 성문 엄폐 효과로 방어/회피율 +15%',
  },
  altar: {
    type: 'altar',
    nameKr: '고대 룬 제단',
    icon: '⛩️',
    walkable: true,
    blocksProjectiles: false,
    baseSpeedMult: 1.0,
    hpRegenPerSec: 8,
    mpRegenPerSec: 10,
    magicAmpPct: 15,
    badgeColor: '#c084fc',
    summaryKr: '✨ 성소 축복! 초당 HP +8 / MP +10 회복 · 마법/치유 위력 +15%',
  },
  crystal_floor: {
    type: 'crystal_floor',
    nameKr: '성역 크리스탈 바닥',
    icon: '💎',
    walkable: true,
    blocksProjectiles: false,
    baseSpeedMult: 1.10,
    mpRegenPerSec: 6,
    magicAmpPct: 10,
    badgeColor: '#818cf8',
    summaryKr: '💎 마력 공명! 이동속도 +10% · 초당 MP +6 회복 · 마법 위력 +10%',
  },
  stone_wall: {
    type: 'stone_wall',
    nameKr: '석조 성벽',
    icon: '🧱',
    walkable: false,
    blocksProjectiles: true,
    baseSpeedMult: 0,
    badgeColor: '#64748b',
    summaryKr: '🚫 이동 불가 · 투사체 및 시야 차단 석조 성벽',
  },
  wooden_wall: {
    type: 'wooden_wall',
    nameKr: '목조 벽체',
    icon: '🪵',
    walkable: false,
    blocksProjectiles: true,
    baseSpeedMult: 0,
    badgeColor: '#92400e',
    summaryKr: '🚫 이동 불가 · 투사체 및 시야 차단 목조 건물 벽',
  },
  house_roof: {
    type: 'house_roof',
    nameKr: '건물 지붕/외벽',
    icon: '🏠',
    walkable: false,
    blocksProjectiles: true,
    baseSpeedMult: 0,
    badgeColor: '#94a3b8',
    summaryKr: '🚫 이동 불가 (건물 내부 진입 불가)',
  },
  megalith: {
    type: 'megalith',
    nameKr: '고대 거석 기둥',
    icon: '🗿',
    walkable: false,
    blocksProjectiles: true,
    baseSpeedMult: 0,
    badgeColor: '#94a3b8',
    summaryKr: '🚫 이동 불가 · 고대 크롬레크 거석 장애물',
  },
  fence: {
    type: 'fence',
    nameKr: '목책 울타리',
    icon: '🚧',
    walkable: false,
    blocksProjectiles: false,
    baseSpeedMult: 0,
    badgeColor: '#b45309',
    summaryKr: '🚫 이동 불가 (목책 경계선)',
  },
};

/**
 * Safely looks up the ZoneTileType at continuous world coordinates (x, y).
 */
export function getTileTypeAt(zone: ZoneData, x: number, y: number): ZoneTileType {
  const tx = Math.max(0, Math.min(zone.width - 1, Math.floor(x + 0.5)));
  const ty = Math.max(0, Math.min(zone.height - 1, Math.floor(y + 0.5)));
  return zone.tiles[ty]?.[tx] || 'grass';
}

/**
 * Computes continuous terrain elevation at (x, y) so `hills_grass` (산지) can determine
 * whether an entity is walking UPHILL (올라가는 쪽: 이속 -20%) or DOWNHILL (내려가는 쪽: 이속 +20%).
 */
export function getTerrainElevationAt(zone: ZoneData, x: number, y: number): number {
  const tile = getTileTypeAt(zone, x, y);
  if (tile === 'water' || tile === 'swamp') return 0.0;
  if (tile === 'hills_grass') {
    // Smooth mountain ridge elevation: higher toward northwest/southwest ridges and local hill crests
    // Specifically for the Solaris Crossroads Ridge at (70, 74) -> Peak at (67, 74), Slope down toward (74, 74)
    const distToTestPeak = Math.hypot(x - 67, y - 74);
    if (distToTestPeak <= 8) {
      return 1.0 + Math.max(0, (8 - distToTestPeak) * 0.45);
    }
    // General hills slope (increases as x decreases and y increases, plus smooth wave)
    return 1.5 + Math.sin(x * 0.18) * 0.8 + Math.cos(y * 0.18) * 0.8 + (160 - x) * 0.015;
  }
  return 1.0;
}

export interface EvaluatedTileEffect {
  spec: TilePropertySpec;
  effectiveSpeedMult: number;
  slopeState: 'uphill' | 'downhill' | 'flat';
  statusLabelKr: string;
}

/**
 * Evaluates the exact movement speed multiplier and slope state for an entity at (x, y)
 * moving in direction (dx, dy).
 */
export function evaluateTileMovementEffect(
  zone: ZoneData,
  x: number,
  y: number,
  dx: number,
  dy: number
): EvaluatedTileEffect {
  const tileType = getTileTypeAt(zone, x, y);
  const spec = TILE_PROPERTY_SPECS[tileType] || TILE_PROPERTY_SPECS.grass;

  if (!spec.walkable) {
    return {
      spec,
      effectiveSpeedMult: 0,
      slopeState: 'flat',
      statusLabelKr: `${spec.icon} ${spec.nameKr}: 이동 불가`,
    };
  }

  // Directional Mountain / Hill Slope Check (`hills_grass`: Uphill -20%, Downhill +20%)
  if (spec.isDirectionalSlope) {
    const moveLen = Math.hypot(dx, dy);
    if (moveLen > 0.001) {
      const normX = dx / moveLen;
      const normY = dy / moveLen;
      const curElev = getTerrainElevationAt(zone, x, y);
      const aheadElev = getTerrainElevationAt(zone, x + normX * 0.75, y + normY * 0.75);
      const diff = aheadElev - curElev;

      if (diff > 0.015) {
        return {
          spec,
          effectiveSpeedMult: 0.80, // 올라가는 쪽: 이속 -20%
          slopeState: 'uphill',
          statusLabelKr: `⛰️ 산지 오르막 (이속 -20%)`,
        };
      } else if (diff < -0.015) {
        return {
          spec,
          effectiveSpeedMult: 1.20, // 내려가는 쪽: 이속 +20%
          slopeState: 'downhill',
          statusLabelKr: `⛰️ 산지 내리막 (이속 +20%)`,
        };
      }
    }
    return {
      spec,
      effectiveSpeedMult: 1.0,
      slopeState: 'flat',
      statusLabelKr: `⛰️ 산지 능선 (오르막 -20% / 내리막 +20%)`,
    };
  }

  const pct = Math.round((spec.baseSpeedMult - 1) * 100);
  const speedTag = pct > 0 ? `이속 +${pct}%` : pct < 0 ? `이속 ${pct}%` : `이속 100%`;

  return {
    spec,
    effectiveSpeedMult: spec.baseSpeedMult,
    slopeState: 'flat',
    statusLabelKr: `${spec.icon} ${spec.nameKr} (${speedTag})`,
  };
}

/**
 * Checks if a target coordinate (nx, ny) is walkable in the current Zone.
 */
export function isTileWalkable(zone: ZoneData, nx: number, ny: number): boolean {
  if (nx < 1 || nx >= zone.width - 1 || ny < 1 || ny >= zone.height - 1) {
    return false;
  }

  // 1. Check Placed Housing Objects (Walls, Fences, Obstacles)
  const placedList = housingSystem.getPlacedObjectsInZone(zone.id);
  const roundX = Math.round(nx);
  const roundY = Math.round(ny);
  for (const obj of placedList) {
    if (obj.isObstacle && Math.round(obj.x) === roundX && Math.round(obj.y) === roundY) {
      return false; // Blocks movement
    }
  }

  // 2. Check Base Zone Terrain Tile
  const tileType = getTileTypeAt(zone, nx, ny);
  const spec = TILE_PROPERTY_SPECS[tileType];
  return spec ? spec.walkable : true;
}

/**
 * Applies per-frame tile hazards (Lava direct damage + Burn DOT, Swamp Poison DOT,
 * Snow/Sand SP drain, Altar/Crystal HP/MP blessing regen) to an entity.
 */
export function applyTileEnvironmentEffects(
  entity: EntityState,
  zone: ZoneData,
  dt: number,
  onHazardTick?: (entity: EntityState, dmg: number, label: string, tileType: ZoneTileType) => void
): void {
  if (entity.isDead || entity.category === 'resource' || entity.category === 'npc') return;

  const tileType = getTileTypeAt(zone, entity.x, entity.y);
  const spec = TILE_PROPERTY_SPECS[tileType];
  if (!spec) return;

  // 1. Apply DOT if stepping on a DOT tile (e.g., Lava Burn DOT or Swamp Poison DOT)
  if (spec.dotEffect) {
    entity.dotEffect = {
      damagePerSec: spec.dotEffect.damagePerSec,
      remaining: spec.dotEffect.duration,
      tickAccum: entity.dotEffect?.tickAccum ?? 0,
      sourceId: `tile_${tileType}`,
      sourceName: spec.dotEffect.name,
    };
  }

  // 2. Direct tile hazard damage (e.g., standing directly on Lava or Swamp)
  if (spec.directDamagePerSec && spec.directDamagePerSec > 0) {
    const accumKey = '__tileHazardAccum' as any;
    const curAccum = ((entity as any)[accumKey] || 0) + dt;
    if (curAccum >= 1.0) {
      (entity as any)[accumKey] = curAccum - 1.0;
      const dmg = Math.round(spec.directDamagePerSec);
      entity.resources.hp = Math.max(1, entity.resources.hp - dmg);
      if (onHazardTick) {
        onHazardTick(entity, dmg, spec.nameKr, tileType);
      }
    } else {
      (entity as any)[accumKey] = curAccum;
    }
  }

  // 3. Process active DOT effect (continues burning/poisoning even after stepping off Lava/Swamp!)
  if (entity.dotEffect && entity.dotEffect.remaining > 0) {
    entity.dotEffect.remaining -= dt;
    entity.dotEffect.tickAccum += dt;
    if (entity.dotEffect.tickAccum >= 1.0) {
      entity.dotEffect.tickAccum -= 1.0;
      const dotDmg = Math.round(entity.dotEffect.damagePerSec);
      entity.resources.hp = Math.max(1, entity.resources.hp - dotDmg);
      if (onHazardTick) {
        onHazardTick(
          entity,
          dotDmg,
          entity.dotEffect.sourceName,
          entity.dotEffect.sourceId.includes('lava') ? 'lava' : 'swamp'
        );
      }
    }
    if (entity.dotEffect.remaining <= 0) {
      entity.dotEffect = null;
    }
  }

  // 4. Tile Blessing Regeneration (Altar, Crystal Floor, Farmland)
  if (spec.hpRegenPerSec && entity.resources.hp < entity.resources.maxHp) {
    entity.resources.hp = Math.min(entity.resources.maxHp, entity.resources.hp + spec.hpRegenPerSec * dt);
  }
  if (spec.mpRegenPerSec && entity.resources.mp < entity.resources.maxMp) {
    entity.resources.mp = Math.min(entity.resources.maxMp, entity.resources.mp + spec.mpRegenPerSec * dt);
  }

  // 5. Cold Snow / Hot Sand SP Drain when moving
  const isMoving = Math.abs(entity.vx) > 0.05 || Math.abs(entity.vy) > 0.05;
  if (isMoving && spec.spDrainPerSec && entity.resources.sp > 0) {
    entity.resources.sp = Math.max(0, entity.resources.sp - spec.spDrainPerSec * dt);
  }
}

// Deep clone defaults for GM reset capability
const DEFAULT_TILE_PROPERTY_SPECS = JSON.parse(JSON.stringify(TILE_PROPERTY_SPECS));

/**
 * Live Admin GM Tile Property Updater
 * Allows real-time modification of tile properties (walkable, speed multiplier, damage, evade, etc.)
 */
export function updateTilePropertySpec(type: ZoneTileType, updates: Partial<TilePropertySpec>): void {
  if (TILE_PROPERTY_SPECS[type]) {
    Object.assign(TILE_PROPERTY_SPECS[type], updates);
  }
}

/**
 * Resets all tile properties back to factory defaults
 */
export function resetTilePropertySpecs(): void {
  for (const key of Object.keys(DEFAULT_TILE_PROPERTY_SPECS) as ZoneTileType[]) {
    TILE_PROPERTY_SPECS[key] = JSON.parse(JSON.stringify(DEFAULT_TILE_PROPERTY_SPECS[key]));
  }
}

export function getAllTileTypes(): ZoneTileType[] {
  return Object.keys(TILE_PROPERTY_SPECS) as ZoneTileType[];
}
